import { UserRole } from '../models/user.model';
import { MasterLocation } from '../models/location.model';

/** Normalise block/NAC labels for comparison (handles spelling + "Block" suffix). */
export function normalizeBlockKey(name: string): string {
  return (name || '')
    .toLowerCase()
    .replace(/dhamnagar|dhamanagar/g, 'dham')
    .replace(/\s*block\b/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

export function userHasUnrestrictedBlockAccess(role: UserRole | string | undefined): boolean {
  return role === UserRole.ADMIN || role === UserRole.MINISTER || role === UserRole.OSD;
}

/** Keys used to match a project row against an assigned master location. */
export function blockMatchKeys(locationName: string, location?: MasterLocation | null): Set<string> {
  const keys = new Set<string>();
  const add = (v: string) => {
    const k = normalizeBlockKey(v);
    if (k) keys.add(k);
  };
  add(locationName);
  for (const a of location?.aliases ?? []) add(a);
  if (locationName.toLowerCase().endsWith(' block')) {
    add(locationName.replace(/\s+block$/i, ''));
  }
  return keys;
}

export function projectMatchesBlockAccess(
  project: { block?: string | null },
  blockAccess: string[] | undefined | null,
  locations: MasterLocation[],
): boolean {
  if (!blockAccess?.length) return false;
  const projectKey = normalizeBlockKey(project.block ?? '');
  if (!projectKey) return false;

  for (const assigned of blockAccess) {
    const loc = locations.find(l => l.name === assigned);
    const keys = blockMatchKeys(assigned, loc);
    if (keys.has(projectKey)) return true;
  }
  return false;
}

/** Compact label for headers — e.g. "Dhamnagar Block" or "Dhamnagar Block +2". */
export function formatBlockAccessLabel(
  blockAccess: string[] | undefined | null,
  unrestricted = false,
): { primary: string; title: string; count: number } {
  if (unrestricted) {
    return { primary: 'All Blocks / NACs', title: 'Full constituency access', count: 0 };
  }
  const list = (blockAccess ?? []).filter(Boolean);
  if (list.length === 0) {
    return { primary: 'No Block Assigned', title: '', count: 0 };
  }
  if (list.length === 1) {
    return { primary: list[0], title: list[0], count: 1 };
  }
  return {
    primary: `${list[0]} +${list.length - 1}`,
    title: list.join(', '),
    count: list.length,
  };
}

/** Project-form block value from a master location name. */
export function projectBlockFromLocation(
  locationName: string,
  location?: MasterLocation | null,
): string {
  if (location?.aliases?.length) return location.aliases[0];
  return locationName.replace(/\s+Block$/i, '').trim() || locationName;
}

export function findMasterLocation(
  locations: MasterLocation[],
  assignedName: string,
): MasterLocation | undefined {
  const raw = (assignedName || '').trim();
  if (!raw) return undefined;
  const key = normalizeBlockKey(raw);
  return locations.find(loc => {
    if (loc.name === raw) return true;
    if ((loc.aliases ?? []).includes(raw)) return true;
    return blockMatchKeys(loc.name, loc).has(key);
  });
}

/** Dropdown values for project forms — from master_locations, scoped by assignment. */
export function formBlockOptionsFromMasters(
  locations: MasterLocation[],
  blockAccess: string[] | null | undefined,
  unrestricted = false,
  fallback: readonly string[] = [],
): string[] {
  const active = locations.filter(l => l.name && l.active !== false);
  const allFromMaster = active.length
    ? active.map(l => projectBlockFromLocation(l.name, l))
    : [...fallback];

  if (unrestricted) {
    return Array.from(new Set(allFromMaster));
  }

  const access = (blockAccess ?? []).map(s => s.trim()).filter(Boolean);
  if (!access.length) {
    return Array.from(new Set(allFromMaster));
  }

  const scoped = access.map(name => {
    const loc = findMasterLocation(active, name);
    return projectBlockFromLocation(loc?.name ?? name, loc);
  });

  return Array.from(new Set(scoped));
}
